// The lifecycle of the Internal Suitability Assessment (SEP-243 sections 30,
// 33 and 35).
//
// The head `suitability_assessments` row is the WORKING DRAFT, and a
// `SuitabilityAssessmentVersion` snapshot locks the content at each sign-off.
// This module holds the closed value set and the PURE rules that read it.
//
// Every rule of the lifecycle lives here, and nowhere else. The ontology
// Actions, the onboarding gate, the controller and the browser form all read
// these functions, so the four cannot disagree about what a value means.
//
// `vendor/septima-ontology-wealth/packages/ontology-wealth/specs/suitability/suitability-assessment-version.md` states the
// rules in prose.

import { z } from 'zod';

/** The eight states of one Internal Review. */
export const SUITABILITY_LIFECYCLE_STATUSES = [
    'draft',
    'awaiting_peer_assignment',
    'in_peer_review',
    'peer_returned',
    'in_compliance_review',
    'compliance_returned',
    'approved',
    'rejected',
] as const;

export const suitabilityLifecycleSchema = z.enum(SUITABILITY_LIFECYCLE_STATUSES);
export type SuitabilityLifecycle = z.infer<typeof suitabilityLifecycleSchema>;

/**
 * The three states in which the form takes an edit.
 *
 * `draft` is the first round. `peer_returned` and `compliance_returned` are the
 * two doors back: a reviewer sent the work back, so the Desk works on it again.
 * Every other state describes a LOCKED version, and a save against one of them
 * is refused.
 */
export const SUITABILITY_EDITABLE_LIFECYCLES = [
    'draft',
    'peer_returned',
    'compliance_returned',
] as const;

/** True when the working draft takes an edit. */
export function isSuitabilityEditable(lifecycle: SuitabilityLifecycle): boolean {
    return (SUITABILITY_EDITABLE_LIFECYCLES as ReadonlyArray<string>).includes(lifecycle);
}

/**
 * The four states in which the onboarding gate `suitability -> signing` clears.
 *
 * The IM signed the review off, and nobody sent it back. `peer_returned`,
 * `compliance_returned` and `rejected` are absent on purpose: the review is
 * with the Desk again, or the firm refused it, so the case must not move on.
 */
const GATE_CLEARING_LIFECYCLES = [
    'awaiting_peer_assignment',
    'in_peer_review',
    'in_compliance_review',
    'approved',
] as const;

/** True when the Internal Review lets the onboarding case reach `signing`. */
export function suitabilityClearsSigningGate(lifecycle: SuitabilityLifecycle): boolean {
    return (GATE_CLEARING_LIFECYCLES as ReadonlyArray<string>).includes(lifecycle);
}

/**
 * The three states from which the responsible investment manager opens an
 * amendment round (SEP-243 sections 33 and 35).
 *
 * `rejected` is absent: the door out of a refusal belongs to Compliance.
 * `in_peer_review` is absent: an open assignment is a read grant that
 * Compliance made, so Compliance takes it back first. The three editable
 * states are absent because the draft is open already. The form reads this
 * function so that it never offers a round that `OpenSuitabilityAmendmentRound`
 * refuses (SEP-258).
 */
const AMENDMENT_ROUND_LIFECYCLES = [
    'awaiting_peer_assignment',
    'in_compliance_review',
    'approved',
] as const;

/** True when the responsible investment manager may open an amendment round. */
export function suitabilityAdmitsAmendmentRound(lifecycle: SuitabilityLifecycle): boolean {
    return (AMENDMENT_ROUND_LIFECYCLES as ReadonlyArray<string>).includes(lifecycle);
}

/**
 * The lifecycle mapped onto the LEGACY two-value `status` column.
 *
 * The column stays for one release, and slice 15 of SEP-243 drops it. Until
 * then the sign-off and the amendment round dual-write it, so an image of the
 * previous release still reads a true value. This function is the one map, and
 * the onboarding derivation reads it too.
 */
export function legacySuitabilityStatus(lifecycle: SuitabilityLifecycle): 'draft' | 'completed' {
    return suitabilityClearsSigningGate(lifecycle) ? 'completed' : 'draft';
}

/**
 * Which version the working draft becomes.
 *
 * An editable draft is the NEXT version, so it counts one above the signed-off
 * rows. A locked review IS the newest signed-off row, so it counts them. The
 * value is computed at each read, and no column holds it.
 */
export function suitabilityWorkingVersionNumber(input: {
    lifecycle: SuitabilityLifecycle;
    signedOffVersionCount: number;
}): number {
    const count = Math.max(0, input.signedOffVersionCount);
    return isSuitabilityEditable(input.lifecycle) ? count + 1 : count;
}

/**
 * The words the screen prints for one lifecycle value.
 *
 * The pill of the Internal Review used to read `Draft` or `Completed`, which
 * the eight states make false: a review in peer review is neither. The label
 * is new copy for a new state, and it replaces a label that this change made
 * untrue.
 */
// Each key is a closed enum VALUE, and a value may be snake_case (CLAUDE.md,
// "Keys are camelCase. Values can be snake_case"). The five snake keys hold an
// entry in `ALLOWED_SNAKE_IDENTIFIERS` of `snake-case-guard.ts`, which is the
// canonical exception list. A quoted key is NOT the answer here: prettier runs
// with `quoteProps: as-needed`, so it takes the quotes off again.
export const SUITABILITY_LIFECYCLE_LABELS: Readonly<Record<SuitabilityLifecycle, string>> = {
    draft: 'Draft',
    awaiting_peer_assignment: 'Awaiting peer assignment',
    in_peer_review: 'In peer review',
    peer_returned: 'Returned by the peer reviewer',
    in_compliance_review: 'In Compliance review',
    compliance_returned: 'Returned by Compliance',
    approved: 'Approved',
    rejected: 'Rejected',
};

// ---------------------------------------------------------------------------
// The mandatory rationale (SEP-257 items 19, 20 and 21)
// ---------------------------------------------------------------------------
//
// The firm's instruction: a recommendation without a written reason is not a
// recommendation the file can defend. Each of the three conclusions therefore
// carries its own reason, and the SAVE refuses while one is blank.
//
//   `riskProfileRecommendation` -> `riskProfileRationale`
//   `strategyRecommendation`    -> `strategyRationale`
//   `serviceRecommendation`     -> `serviceRationale`
//
// Whitespace is blank. A reason of three spaces states nothing.
//
// # The tension with item 22 of the card
//
// Item 22 of the same card reads that the assessment "can continue to be saved
// as a draft while the rationale is outstanding". Items 19 to 21 are the newer
// and the explicit ask, so they win: a recommendation with no reason does not
// save, in a draft or anywhere else. The IM saves a draft that holds NO
// recommendation as often as they like, so the draft is not blocked; only the
// pair of a conclusion and a silence is. The firm must settle the two lines of
// the card, and this comment records the disagreement until it does.
//
// The rule is pure and it lives here alone. `SaveSuitabilityAssessment` is the
// gate (ADR-0002), the controller maps its refusal, and the form reads the same
// function so the screen and the refusal cannot disagree.

/** The three conclusions the review draws, each with the reason it needs. */
export const SUITABILITY_RATIONALE_PAIRS = [
    {
        recommendationField: 'riskProfileRecommendation',
        rationaleField: 'riskProfileRationale',
        label: 'Risk Profile Recommendation',
        rationaleLabel: 'Risk profile rationale',
    },
    {
        recommendationField: 'strategyRecommendation',
        rationaleField: 'strategyRationale',
        label: 'Strategy Recommendation',
        rationaleLabel: 'Strategy rationale',
    },
    {
        recommendationField: 'serviceRecommendation',
        rationaleField: 'serviceRationale',
        label: 'Service Recommendation',
        rationaleLabel: 'Service rationale',
    },
] as const;

export type SuitabilityRationalePair = (typeof SUITABILITY_RATIONALE_PAIRS)[number];

/** The words the field prints, and the words the refusal names. */
export const SUITABILITY_RATIONALE_REQUIRED_MESSAGE =
    'A rationale is required when a recommendation is recorded.';

/** The six fields the rule reads. Every one is optional and nullable. */
export type SuitabilityRationaleForm = {
    readonly [K in
        | SuitabilityRationalePair['recommendationField']
        | SuitabilityRationalePair['rationaleField']]?: string | null;
};

/** One conclusion that holds a recommendation and no reason. */
export interface SuitabilityRationaleGap {
    readonly recommendationField: SuitabilityRationalePair['recommendationField'];
    readonly rationaleField: SuitabilityRationalePair['rationaleField'];
    /** The label of the recommendation field, as the form prints it. */
    readonly label: string;
    /** The label of the rationale field, as the form prints it. */
    readonly rationaleLabel: string;
}

/** True when the value holds no character other than whitespace. */
function isBlank(value: string | null | undefined): boolean {
    return (value ?? '').trim() === '';
}

/**
 * Every recommendation that carries no reason.
 *
 * The list is empty when the review is savable. A recommendation that is not
 * set raises nothing: the IM has reached no conclusion, so no reason is due.
 */
export function rationaleGaps(
    form: SuitabilityRationaleForm,
): ReadonlyArray<SuitabilityRationaleGap> {
    const gaps: SuitabilityRationaleGap[] = [];
    for (const pair of SUITABILITY_RATIONALE_PAIRS) {
        if (isBlank(form[pair.recommendationField])) continue;
        if (!isBlank(form[pair.rationaleField])) continue;
        gaps.push({
            recommendationField: pair.recommendationField,
            rationaleField: pair.rationaleField,
            label: pair.label,
            rationaleLabel: pair.rationaleLabel,
        });
    }
    return gaps;
}

/**
 * The refusal, in one sentence that names each missing reason. It is null when
 * the review holds no gap.
 */
export function rationaleGapMessage(form: SuitabilityRationaleForm): string | null {
    const gaps = rationaleGaps(form);
    if (gaps.length === 0) return null;
    const names = gaps.map((gap) => gap.rationaleLabel).join(', ');
    return `${SUITABILITY_RATIONALE_REQUIRED_MESSAGE} The review is missing: ${names}.`;
}
